"use no memo";

import { isInteractiveDialog } from "@choose/ui";
import { Badge } from "@choose/ui/badge";
import { Checkbox } from "@choose/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@choose/ui/hover-card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@choose/ui/tooltip";
import type {
  ColumnDef,
  ColumnVisibilityState,
  ExpandedState,
  GroupingState,
  Row,
  RowSelectionState,
  TableState,
} from "@tanstack/react-table";
import {
  cellSelectionFeature,
  columnFilteringFeature,
  columnGroupingFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  createGroupedRowModel,
  createSortedRowModel,
  metaHelper,
  rowAggregationFeature,
  rowExpandingFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";
import DOMPurify from "dompurify";
import isEqual from "lodash-es/isEqual";
import {
  BookSearchIcon,
  CalendarClockIcon,
  CircleDashedIcon,
  TargetIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import { UserPreview } from "~/components/common/previews/user-preview";
import { TicketChannel } from "~/components/common/ticket/ticket-channel";
import { TicketActionsToolbar } from "~/components/common/tickets/ticket-actions-toolbar";
import { TicketGroupList } from "~/components/common/tickets/ticket-group-list";
import { TicketListVirtual } from "~/components/common/tickets/ticket-list-virtual";
import { TicketSimpleList } from "~/components/common/tickets/ticket-simple-list";
import { Assignee, AssigneeAvatar } from "~/components/ui/assignee";
import { RelativeTime } from "~/components/ui/relative-time";
import type {
  SupportTicketStatus,
  SupportTicketSubStatus,
} from "~/graphql/graphql.gen";
import { SupportTicketChannel } from "~/graphql/graphql.gen";
import { useSelectRows } from "~/hooks/use-select-rows";
import { getActorFullName } from "~/stores/contacts-store";
import { getFlow } from "~/stores/flows-store";
import { getStatuses } from "~/stores/ticket-statuses-store";
import { getSubStatuses } from "~/stores/ticket-sub-statuses-store";
import type { Ticket } from "~/stores/tickets-store";
import { getNoAssigneeUser } from "~/stores/users-store";

export const NEXT_ITEM_SHORTCUT = "ArrowDown";
export const PREVIOUS_ITEM_SHORTCUT = "ArrowUp";
export const SELECT_ITEM_SHORTCUT = "x";
export const BULK_SELECT_ITEM_SHORTCUT = "shift+x";

interface MyColumnMeta {
  layout: string;
}

export const features = tableFeatures({
  cellSelectionFeature,
  columnFilteringFeature,
  columnGroupingFeature,
  columnMeta: metaHelper<MyColumnMeta>(),
  columnOrderingFeature,
  columnPinningFeature,
  columnVisibilityFeature,
  filteredRowModel: createFilteredRowModel(),
  groupedRowModel: createGroupedRowModel(),
  sortedRowModel: createSortedRowModel(),
  rowAggregationFeature,
  rowExpandingFeature,
  rowSelectionFeature,
  rowSortingFeature,
});

const ROW_SELECTOR = "a[data-slot^=data-list-row]";

export const TABLE_ACCESSOR_ASSIGNEE = "assignedTo";
export const TABLE_ACCESSOR_CHANNEL = "channel";
export const TABLE_ACCESSOR_CONTACT = "contact";
export const TABLE_ACCESSOR_FLOW = "flow";
export const TABLE_ACCESSOR_HAS_PENDING_SCHEDULE_ACTIONS =
  "hasPendingScheduleActions";
export const TABLE_ACCESSOR_IS_DRAFT = "isDraft";
export const TABLE_ACCESSOR_ORDER = "order";
export const TABLE_ACCESSOR_ORDER_FLOW = "orderFlow";
export const TABLE_ACCESSOR_PREVIEW_TEXT = "previewText";
export const TABLE_ACCESSOR_SALE_ID = "saleId";
export const TABLE_ACCESSOR_SALE_NAME = "saleName";
export const TABLE_ACCESSOR_SALES_TEAM = "salesTeam";
export const TABLE_ACCESSOR_SALE_TYPE = "saleType";
export const TABLE_ACCESSOR_STATUS = "status";
export const TABLE_ACCESSOR_STATUS_UPDATED_AT = "statusUpdatedAt";
export const TABLE_ACCESSOR_STORE = "store";
export const TABLE_ACCESSOR_SUPPLIER = "supplier";
export const TABLE_ACCESSOR_SUB_STATUS = "subStatus";
export const TABLE_ACCESSOR_TITLE = "title";

export const DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityState = {
  saleName: true,
  subStatus: true,
  statusUpdatedAt: true,
  contact: true,
  status: false,
  saleId: false,
  salesTeam: false,
  saleType: false,
  store: false,
  order: false,
  orderFlow: false,
  supplier: false,
  flow: false,
};

export const DEFAULT_COLUMN_ORDER = [
  "select",
  TABLE_ACCESSOR_SUB_STATUS,
  TABLE_ACCESSOR_CONTACT,
  TABLE_ACCESSOR_SALE_NAME,
  TABLE_ACCESSOR_TITLE,
  TABLE_ACCESSOR_PREVIEW_TEXT,
  "alt",
  TABLE_ACCESSOR_ORDER_FLOW,
  TABLE_ACCESSOR_HAS_PENDING_SCHEDULE_ACTIONS,
  TABLE_ACCESSOR_IS_DRAFT,
  TABLE_ACCESSOR_CHANNEL,
  TABLE_ACCESSOR_STATUS_UPDATED_AT,
  TABLE_ACCESSOR_ASSIGNEE,
];

export function TicketList({
  data,
  hasFilters,
  hasMore,
  isFetchingMore = false,
  onFetchMore,
  state,
}: {
  data: Ticket[];
  hasFilters?: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onFetchMore?: () => void;
  state: Partial<TableState<typeof features>> | undefined;
}) {
  const { t } = useTranslation();
  const statuses = getStatuses(t);
  const subStatuses = getSubStatuses(t);
  const [grouping, setGrouping] = useState<GroupingState>(
    state?.grouping ?? [],
  );
  const [expanded, setExpanded] = useState<ExpandedState>(true);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Expand all groups when grouping changes
  useEffect(() => {
    if (!isEqual(grouping, state?.grouping ?? [])) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setGrouping(state?.grouping ?? []);
      setExpanded(true);
    }
  }, [grouping, state?.grouping]);

  const selectRows = useSelectRows<Row<typeof features, Ticket>>();

  const columns = useMemo<ColumnDef<typeof features, Ticket>[]>(
    () => [
      {
        id: "select",
        cell: ({ row, table }) => (
          <div
            className="flex items-center justify-center pl-1.5"
            data-list-grid-column="select"
          >
            <Checkbox
              tabIndex={-1}
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                onClick: (e) => {
                  e.preventDefault();

                  // Focus the row element for keyboard navigation
                  const rowElement = (e.target as HTMLElement).closest(
                    ROW_SELECTOR,
                  );
                  if (rowElement) (rowElement as HTMLAnchorElement).focus();

                  selectRows(table.getRowModel(), row, e.shiftKey);
                },
              }}
              className="relative opacity-0 after:absolute after:inset-[-15px_-8px_-15px_-10px] hover:opacity-100 data-checked:opacity-100"
            />
          </div>
        ),
        meta: {
          layout: "start",
        },
      },
      {
        id: TABLE_ACCESSOR_STATUS,
        accessorKey: "status",
        header: () => "Status",
        aggregatedCell: ({ cell }) => {
          const status = statuses.find(
            (status) => status.value === cell.getValue<SupportTicketStatus>(),
          );

          if (!status) return null;

          return (
            <div className="flex gap-2">
              <status.icon className="size-4" style={{ color: status.color }} />
              {status.label}
            </div>
          );
        },
      },
      {
        id: TABLE_ACCESSOR_CONTACT,
        accessorKey: "contact.id",
        header: () => "Contact",
        cell: ({ row }) => {
          return (
            <div
              className="flex items-center text-sm"
              data-list-grid-column="contact"
            >
              <span className="truncate">
                {getActorFullName(row.original.contact)}
              </span>
            </div>
          );
        },
        meta: {
          layout: "start",
        },
      },
      {
        id: TABLE_ACCESSOR_SALE_NAME,
        accessorKey: "contextDerived.sale.name",
        header: () => "Sale",
        cell: ({ cell }) => {
          return (
            <div
              className="flex items-center text-sm"
              data-list-grid-column="saleName"
            >
              <span className="truncate">
                {cell.getValue<string | undefined>() ?? t("sale.no_sale")}
              </span>
            </div>
          );
        },
        meta: {
          layout: "start",
        },
      },
      {
        id: TABLE_ACCESSOR_SUB_STATUS,
        accessorKey: "subStatus",
        header: () => "Status Detail",
        aggregatedCell: ({ cell }) => {
          const subStatus = subStatuses.find(
            (status) =>
              status.value === cell.getValue<SupportTicketSubStatus>(),
          );

          if (!subStatus) return null;

          return (
            <div className="flex items-center gap-2">
              <subStatus.icon
                className="size-4"
                style={{ color: subStatus.color }}
              />
              {subStatus.label}
            </div>
          );
        },
        cell: ({ cell }) => {
          const subStatus = subStatuses.find(
            (status) =>
              status.value === cell.getValue<SupportTicketSubStatus>(),
          );

          if (!subStatus) return null;

          return (
            <div
              className="flex items-center gap-2"
              data-list-grid-column="subStatus"
            >
              <subStatus.icon
                className="size-4"
                style={{ color: subStatus.color }}
              />
            </div>
          );
        },
        meta: {
          layout: "start",
        },
      },
      {
        id: TABLE_ACCESSOR_TITLE,
        accessorKey: "title",
        header: () => "Title",
        cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return <span className="mr-1 text-sm">{value}</span>;
        },
        meta: {
          layout: "title",
        },
      },
      {
        id: TABLE_ACCESSOR_PREVIEW_TEXT,
        accessorKey: "previewText",
        header: () => "Preview Text",
        cell: ({ cell, row }) => {
          const value = cell.getValue<string>();
          return (
            <div
              className="text-muted-foreground inline text-sm"
              data-content="markdown-inline"
            >
              <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                  rehypeRaw,
                  [rehypeExternalLinks, { target: "_blank" }],
                ]}
                components={{
                  a: ({ children }) => children,
                }}
              >
                {row.original.channel === SupportTicketChannel.AppChat
                  ? value
                  : DOMPurify.sanitize(value, {
                      FORBID_TAGS: ["style"],
                      FORBID_ATTR: ["style"],
                    })}
              </Markdown>
            </div>
          );
        },
        meta: {
          layout: "title",
        },
      },
      {
        accessorKey: "alt",
        cell: () => {
          return <div className="flex flex-[initial] grow"></div>;
        },
      },
      {
        id: TABLE_ACCESSOR_IS_DRAFT,
        accessorKey: "isDraft",
        cell: ({ cell }) => {
          if (!cell.getValue<boolean>()) return null;
          return (
            <span className="flex flex-[initial] shrink-0 items-center">
              <Badge className="bg-yellow-600">{t("ticket.draft")}</Badge>
            </span>
          );
        },
        meta: {
          layout: "labels",
        },
      },
      {
        id: TABLE_ACCESSOR_HAS_PENDING_SCHEDULE_ACTIONS,
        accessorKey: "hasPendingScheduleActions",
        cell: ({ cell }) => {
          if (!cell.getValue<boolean>()) return <div className="size-4"></div>;
          return (
            <span className="flex shrink-0 items-center gap-1">
              <span className="text-muted-foreground [&>svg]:size-4">
                <CalendarClockIcon />
              </span>
            </span>
          );
        },
        meta: {
          layout: "labels",
        },
      },
      {
        id: TABLE_ACCESSOR_CHANNEL,
        accessorKey: "channel",
        cell: ({ cell }) => {
          const value = cell.getValue<SupportTicketChannel>();
          return <TicketChannel channel={value} variant="small" />;
        },
        meta: {
          layout: "channel",
        },
      },
      {
        id: TABLE_ACCESSOR_STATUS_UPDATED_AT,
        accessorKey: "statusUpdatedAt",
        cell: ({ cell }) => (
          <div
            className="flex items-center justify-end text-sm"
            data-list-grid-column="statusUpdatedAt"
          >
            <Tooltip>
              <TooltipTrigger>
                <RelativeTime dateTime={new Date(cell.getValue<string>())} />
              </TooltipTrigger>
              <TooltipContent>
                <p>{new Date(cell.getValue<string>()).toUTCString()}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        ),
        meta: {
          layout: "end",
        },
      },
      {
        id: TABLE_ACCESSOR_ASSIGNEE,
        accessorFn: (row) => row.assignedTo?.id ?? null,
        aggregatedCell: ({ row }) => {
          const assignedTo = row.original.assignedTo;
          const noAssigneeUser = getNoAssigneeUser(t);

          return (
            <div>
              {assignedTo ? (
                <Assignee assignee={assignedTo} />
              ) : (
                <div className="flex items-center gap-2">
                  <noAssigneeUser.icon />
                  {noAssigneeUser.label}
                </div>
              )}
            </div>
          );
        },
        cell: ({ row }) => {
          const assignedTo = row.original.assignedTo;
          return (
            <div className="flex flex-[initial] shrink-0 items-center">
              {assignedTo ? (
                <HoverCard>
                  <HoverCardTrigger
                    render={
                      <div>
                        <AssigneeAvatar assignee={assignedTo} size="default" />
                      </div>
                    }
                  />
                  <HoverCardContent>
                    <UserPreview userId={assignedTo.id} />
                  </HoverCardContent>
                </HoverCard>
              ) : (
                <CircleDashedIcon className="size-5" />
              )}
            </div>
          );
        },
        meta: {
          layout: "assignedTo",
        },
      },
      {
        id: TABLE_ACCESSOR_STORE,
        accessorKey: "contextDerived.storeId",
      },
      {
        id: TABLE_ACCESSOR_SALE_ID,
        accessorKey: "contextDerived.saleId",
        aggregatedCell: ({ row }) => {
          return (
            <div className="flex gap-2">
              <span className="truncate">
                {row.original.contextDerived?.sale?.name ?? t("sale.no_sale")}
              </span>
            </div>
          );
        },
      },
      {
        id: TABLE_ACCESSOR_SALES_TEAM,
        accessorKey: "contextDerived.sale.salesTeamName",
      },
      {
        id: TABLE_ACCESSOR_SALE_TYPE,
        accessorKey: "contextDerived.sale.type",
      },
      {
        id: TABLE_ACCESSOR_ORDER,
        accessorKey: "contextDerived.orderId",
      },
      {
        id: TABLE_ACCESSOR_SUPPLIER,
        accessorKey: "contextDerived.supplierId",
      },
      {
        id: TABLE_ACCESSOR_FLOW,
        accessorFn: (row) => getFlow(row.initialRequest),
      },
      {
        id: TABLE_ACCESSOR_ORDER_FLOW,
        accessorKey: "orderFlowId",
        cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return null;

          return <TargetIcon className="size-4 shrink-0" />;
        },
        meta: {
          layout: "orderFlow",
        },
      },
      {
        accessorKey: "end-padding",
        cell: () => <div data-list-grid-column="end-padding"></div>,
        meta: {
          layout: "end",
        },
      },
    ],
    [selectRows, statuses, t, subStatuses],
  );

  const columnVisibility: ColumnVisibilityState = useMemo(
    () => ({
      ...(state?.columnVisibility ?? {}),
      ["select"]: true,
      ["title"]: true,
      ["end-padding"]: true,
    }),
    [state?.columnVisibility],
  );

  const table = useTable({
    features,
    data,
    columns,
    state: {
      ...state,
      columnOrder: [
        "select",
        TABLE_ACCESSOR_SUB_STATUS,
        TABLE_ACCESSOR_CONTACT,
        TABLE_ACCESSOR_SALE_NAME,
        TABLE_ACCESSOR_TITLE,
        TABLE_ACCESSOR_PREVIEW_TEXT,
        TABLE_ACCESSOR_IS_DRAFT,
        TABLE_ACCESSOR_ORDER_FLOW,
        TABLE_ACCESSOR_CHANNEL,
        TABLE_ACCESSOR_ASSIGNEE,
        TABLE_ACCESSOR_STATUS_UPDATED_AT,
      ],
      expanded: expanded,
      rowSelection,
    },
    enableExpanding: true,
    enableRowSelection: true,
    groupedColumnMode: false,
    manualFiltering: true,
    manualExpanding: true,
    manualSorting: true,
    getRowId: (row) => row.id,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    debugTable: true,
  });

  useHotkeys(
    SELECT_ITEM_SHORTCUT,
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();

      if (!(e.target instanceof Element)) return;

      const rowId = (e.target as HTMLAnchorElement).getAttribute("data-row-id");

      if (!rowId) return;

      selectRows(table.getRowModel(), table.getRow(rowId), false);
    },
    {
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );

  useHotkeys(
    BULK_SELECT_ITEM_SHORTCUT,
    (e) => {
      e.preventDefault();

      if (!(e.target instanceof Element)) return;

      const rowId = (e.target as HTMLAnchorElement).getAttribute("data-row-id");

      if (!rowId) return;

      selectRows(table.getRowModel(), table.getRow(rowId), true);
    },
    {
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );

  useHotkeys(
    `${PREVIOUS_ITEM_SHORTCUT}, ${NEXT_ITEM_SHORTCUT}`,
    (e) => {
      e.preventDefault();

      if (e.key === PREVIOUS_ITEM_SHORTCUT) {
        if (!(e.target instanceof Element)) return;

        const currentItem = e.target.closest(ROW_SELECTOR);
        if (!currentItem) {
          const firstItem = document.querySelector(ROW_SELECTOR);

          if (firstItem) (firstItem as HTMLAnchorElement).focus();
        } else {
          const items = [...document.querySelectorAll(ROW_SELECTOR)];
          const previousItem = items[items.indexOf(currentItem) - 1];
          const itemToScrollTo = items[items.indexOf(currentItem) - 2];
          if (itemToScrollTo)
            itemToScrollTo.scrollIntoView({ block: "nearest" });

          if (previousItem && previousItem instanceof HTMLAnchorElement)
            previousItem.focus();
        }
      } else if (e.key === NEXT_ITEM_SHORTCUT) {
        if (!(e.target instanceof Element)) return;

        const currentItem = e.target.closest(ROW_SELECTOR);
        if (!currentItem) {
          const firstItem = document.querySelector(ROW_SELECTOR);

          if (firstItem) (firstItem as HTMLAnchorElement).focus();
        } else {
          const items = [...document.querySelectorAll(ROW_SELECTOR)];
          const nextItem = items[items.indexOf(currentItem) + 1];
          const itemToScrollTo = items[items.indexOf(currentItem) + 2];
          if (itemToScrollTo)
            itemToScrollTo.scrollIntoView({ block: "nearest" });

          if (nextItem && nextItem instanceof HTMLAnchorElement)
            nextItem.focus();
        }
      }
    },
    {
      ignoreEventWhen: (e) => isInteractiveDialog(e),
    },
  );

  const visibilityTemplate = useMemo(
    () => ({
      "[select] 26px": true,
      "[subStatus] 16px": columnVisibility[TABLE_ACCESSOR_SUB_STATUS] ?? false,
      "[contact] 128px": columnVisibility[TABLE_ACCESSOR_CONTACT] ?? false,
      "[saleName] 128px": columnVisibility[TABLE_ACCESSOR_SALE_NAME] ?? false,
      "[title] 1fr": true,
      "[statusUpdatedAt] minmax(56px, auto)":
        columnVisibility[TABLE_ACCESSOR_STATUS_UPDATED_AT] ?? false,
      "[end-padding] 12px": true,
    }),
    [columnVisibility],
  );

  const gridListStyle = useMemo(
    () =>
      ({
        "--data-list-template": Object.entries(visibilityTemplate)
          .filter(([_, isVisible]) => isVisible)
          .map(([template]) => template)
          .join(" "),
      }) as React.CSSProperties,
    [visibilityTemplate],
  );

  const rows = table.getRowModel().rows;

  if (!hasFilters && rows.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("tickets.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("tickets.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("tickets.search.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("tickets.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const isInfiniteMode = typeof onFetchMore === "function";

  if (isInfiniteMode) {
    return (
      <>
        <div
          className="col-[1/_-1] grid size-full min-w-0 grow grid-cols-(--data-list-template) gap-2 overflow-hidden"
          style={gridListStyle}
        >
          <TicketListVirtual
            hasMore={hasMore}
            isFetchingMore={isFetchingMore}
            onFetchMore={onFetchMore}
            rows={rows}
          />
        </div>
        <TicketActionsToolbar
          rows={rows}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      </>
    );
  }

  if (state?.grouping?.length) {
    return (
      <>
        <div
          className="col-[1/_-1] grid size-full min-w-0 grow grid-cols-(--data-list-template) gap-2 overflow-hidden"
          style={gridListStyle}
        >
          <TicketGroupList rows={rows} setExpanded={setExpanded} />
        </div>
        <TicketActionsToolbar
          rows={rows}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      </>
    );
  }

  return (
    <>
      <div
        className="col-[1/_-1] grid size-full min-w-0 grow grid-cols-(--data-list-template) gap-2 overflow-hidden"
        style={gridListStyle}
      >
        <TicketSimpleList rows={rows} />
      </div>
      <TicketActionsToolbar
        rows={rows}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
      />
    </>
  );
}
